import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/feature/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const techniques = [
  { icon: 'ri-refresh-line', name: 'Técnica Swish', desc: 'Reprograma padrões automáticos em minutos' },
  { icon: 'ri-anchor-line', name: 'Ancoragem Emocional', desc: 'Acesse estados positivos instantaneamente' },
  { icon: 'ri-fire-line', name: 'Encantations', desc: 'Reprograma o sistema nervoso com o corpo' },
  { icon: 'ri-time-line', name: 'Linha do Tempo', desc: 'Libera emoções presas no passado' },
  { icon: 'ri-eye-line', name: 'Ressignificação', desc: 'Muda o significado de qualquer experiência' },
  { icon: 'ri-settings-3-line', name: 'Submodalidades', desc: 'O painel de controle da mente humana' },
];

const testimonials = [
  {
    name: 'Camila F.',
    role: 'Analista, SP',
    avatar: 'https://public.readdy.ai/ai/img_res/0210faaf6d8d79bc14043db66a52c65d.jpg',
    text: 'Em 3 semanas de prática minha ansiedade no trabalho desapareceu. Fiz a melhor apresentação da minha carreira. O investimento pagou em 1 dia.',
    result: 'Ansiedade eliminada',
    stars: 5,
  },
  {
    name: 'Marcos O.',
    role: 'Empresário, RJ',
    avatar: 'https://readdy.ai/api/search-image?query=confident%20professional%20brazilian%20man%20smiling%20soft%20cream%20background%20natural%20portrait%2040%20years%20old%20business%20casual%20minimalist&width=56&height=56&seq=lp02&orientation=squarish',
    text: 'Fechei 3 contratos grandes em 60 dias — algo que eu sabotava há anos. As técnicas de PNL removeram exatamente os bloqueios que eu nem sabia que tinha.',
    result: '+3 contratos em 60 dias',
    stars: 5,
  },
  {
    name: 'Juliana S.',
    role: 'Professora, MG',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20brazilian%20woman%20teacher%20smiling%20soft%20cream%20background%20natural%20portrait%2038%20years%20old%20intellectual%20minimalist&width=56&height=56&seq=lp03&orientation=squarish',
    text: 'Carregava mágoa de um relacionamento por 5 anos. A Linha do Tempo resolveu em uma tarde o que terapia não resolveu em 2 anos. Impressionante.',
    result: 'Liberdade emocional',
    stars: 5,
  },
];

const faqs = [
  {
    q: 'Preciso ter experiência em PNL para fazer?',
    a: 'Não. O pacote foi desenhado para iniciantes. Você recebe o passo a passo completo de cada técnica, com exemplos práticos e linguagem acessível.',
  },
  {
    q: 'Quanto tempo leva para ver resultados?',
    a: 'A maioria das pessoas relata mudanças perceptíveis já na primeira semana de prática consistente. Algumas técnicas como o Swish produzem resultados na primeira sessão.',
  },
  {
    q: 'O acesso é vitalício mesmo?',
    a: 'Sim. Você paga uma única vez e tem acesso para sempre — incluindo atualizações futuras do conteúdo, sem nenhuma taxa adicional.',
  },
  {
    q: 'E se eu não gostar?',
    a: 'Garantia incondicional de 7 dias. Se por qualquer motivo você não ficar satisfeita, devolvemos 100% do valor sem perguntas e sem burocracia.',
  },
];

export default function LandingPageExercicios() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState(1800); // 30 min

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="6 Técnicas Completas de PNL | Transformação Garantida — Bia Holmes"
        description="Desbloqueie os 6 protocolos completos de PNL por R$47. Inclui Técnica Swish, Ancoragem Emocional, Linha do Tempo e mais. Acesso vitalício + garantia de 7 dias."
        keywords="PNL técnicas completas, programação neurolinguística, Bia Holmes, transformação pessoal, ancoragem emocional, técnica swish"
        canonical="/landingpage-exercicios"
        schema={[{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Pacote 6 Técnicas Completas de PNL',
          description: 'Protocolos completos de PNL com áudios guiados e workbooks.',
          offers: {
            '@type': 'Offer',
            price: '47.00',
            priceCurrency: 'BRL',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/landingpage-exercicios`,
          },
        }]}
      />

      {/* Header mínimo — sem distrações */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-charcoal-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
              alt="Bia Holmes"
              className="w-10 h-10 rounded-full object-cover border border-gold-200/60"
            />
            <span className="font-display text-base font-semibold text-charcoal-800">
              Bia Holmes
            </span>
          </Link>

          {/* Countdown no header */}
          <div className="hidden sm:flex items-center gap-3 bg-charcoal-900 rounded-full px-4 py-2">
            <i className="ri-time-line text-gold-400 text-sm animate-pulse"></i>
            <span className="text-white/60 text-xs">Oferta expira em</span>
            <span className="font-mono text-sm font-bold text-gold-400">{formatTime(timeLeft)}</span>
          </div>

          <Link
            to="/planos#exercicios-pnl"
            className="px-5 py-2.5 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-full font-bold text-xs tracking-wide hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap"
          >
            Quero por R$ 47
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 pt-20 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-gold-500/5 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_40%_30%,_rgba(201,164,64,0.08)_0%,_transparent_60%)]"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/12 border border-gold-500/25 rounded-full text-gold-400 text-xs font-semibold tracking-widest uppercase mb-8">
            <i className="ri-brain-line"></i>
            6 técnicas de PNL comprovadas
          </div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-6">
            Pare de deixar<br />
            sua mente te<br />
            <span className="text-gold-400">sabotar.</span>
          </h1>

          <p className="text-xl md:text-2xl text-white/60 font-light leading-relaxed max-w-2xl mx-auto mb-10">
            Os 6 protocolos completos de PNL que já transformaram mais de{' '}
            <span className="text-white font-semibold">10.000 vidas</span> — com passo a passo detalhado, áudios guiados e workbooks prontos para usar.
          </p>

          {/* Prova social rápida */}
          <div className="flex flex-wrap justify-center gap-6 mb-12">
            {[
              { icon: 'ri-group-line', text: '10.347 alunos' },
              { icon: 'ri-star-fill', text: '4.9 / 5.0' },
              { icon: 'ri-shield-check-line', text: 'Garantia 7 dias' },
              { icon: 'ri-infinity-line', text: 'Acesso vitalício' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 text-white/55 text-sm">
                <i className={`${item.icon} text-gold-500`}></i>
                {item.text}
              </div>
            ))}
          </div>

          {/* CTA Principal */}
          <div className="flex flex-col items-center gap-4">
            <Link
              to="/planos#exercicios-pnl"
              className="px-14 py-5 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-full font-bold text-lg tracking-wide hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap"
            >
              Quero os 6 Protocolos por R$ 47
            </Link>
            <div className="flex items-center gap-4 text-xs text-white/35">
              <span className="flex items-center gap-1.5">
                <i className="ri-lock-line text-gold-500/60"></i>Pagamento seguro
              </span>
              <span className="w-px h-3 bg-white/15"></span>
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-gold-500/60"></i>Garantia de 7 dias
              </span>
              <span className="w-px h-3 bg-white/15"></span>
              <span className="flex items-center gap-1.5">
                <i className="ri-download-line text-gold-500/60"></i>Acesso imediato
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Barra de urgência */}
      <div className="bg-charcoal-900 border-b border-gold-500/15">
        <div className="max-w-4xl mx-auto px-6 py-3 flex flex-wrap items-center justify-center gap-4 text-center">
          <span className="text-white/60 text-sm">
            <i className="ri-time-line text-gold-400 mr-1.5"></i>
            Desconto de 56% — de R$108 por apenas
          </span>
          <span className="font-display text-xl font-bold text-gold-400">R$ 47</span>
          <span className="text-white/40 text-xs">|</span>
          <span className="text-white/60 text-sm flex items-center gap-1.5">
            <i className="ri-alarm-line text-rose-400 text-sm animate-pulse"></i>
            Expira em <span className="font-mono font-bold text-rose-400 ml-1">{formatTime(timeLeft)}</span>
          </span>
        </div>
      </div>

      {/* O que você vai receber */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-charcoal-900 mb-4 leading-tight">
              6 protocolos completos.<br />
              <span className="text-gold-600">Uma transformação real.</span>
            </h2>
            <p className="text-charcoal-500 text-lg font-light max-w-xl mx-auto">
              Cada técnica é um protocolo completo — não um resumo. Você recebe exatamente o que os melhores coaches cobram R$500 a hora para ensinar.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {techniques.map((tech, i) => (
              <div
                key={tech.name}
                className="bg-cream-50 border border-charcoal-100 rounded-2xl p-6 hover:border-gold-300 transition-all duration-300 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white border border-charcoal-100 group-hover:border-gold-200 group-hover:bg-gold-50 transition-all flex-shrink-0">
                    <i className={`${tech.icon} text-xl text-charcoal-400 group-hover:text-gold-600 transition-colors`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-mono text-charcoal-300 mb-1">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <h3 className="font-display text-base font-bold text-charcoal-800 mb-1.5 leading-snug">
                      {tech.name}
                    </h3>
                    <p className="text-sm text-charcoal-500 font-light leading-relaxed">
                      {tech.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bônus */}
          <div className="bg-gradient-to-br from-charcoal-900 to-charcoal-950 rounded-3xl p-8 text-center border border-gold-500/10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold-500/12 border border-gold-500/20 rounded-full text-gold-400 text-xs font-semibold tracking-widest uppercase mb-5">
              <i className="ri-gift-line"></i>
              Bônus inclusos
            </div>
            <h3 className="font-display text-2xl font-bold text-white mb-8">
              Além dos protocolos, você recebe:
            </h3>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: 'ri-headphone-line', title: 'Áudios Guiados', desc: '6 meditações de PNL em MP3 de alta qualidade' },
                { icon: 'ri-file-pdf-line', title: 'Workbooks PDF', desc: 'Cadernos de exercícios para imprimir e usar' },
                { icon: 'ri-vip-crown-line', title: 'Atualizações Vitalícias', desc: 'Todo novo conteúdo chega sem custo adicional' },
              ].map((bonus) => (
                <div key={bonus.title} className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gold-500/12 border border-gold-500/20 mx-auto mb-3">
                    <i className={`${bonus.icon} text-xl text-gold-400`}></i>
                  </div>
                  <h4 className="font-semibold text-white text-sm mb-2">{bonus.title}</h4>
                  <p className="text-white/45 text-xs font-light leading-relaxed">{bonus.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparação grátis vs completo */}
      <section className="py-20 px-6 bg-cream-50 border-t border-charcoal-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold text-charcoal-900 mb-3">
              Gratuito vs Completo
            </h2>
            <p className="text-charcoal-500 font-light">
              Os exercícios gratuitos são uma amostra. O completo é a transformação.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-charcoal-100 overflow-hidden">
            <div className="grid grid-cols-3 bg-charcoal-900 px-6 py-4">
              <div className="text-white/60 text-sm font-medium">Recurso</div>
              <div className="text-center text-white/60 text-sm font-medium">Gratuito</div>
              <div className="text-center text-gold-400 text-sm font-bold">Completo (R$47)</div>
            </div>
            {[
              ['Passos de cada técnica', '2–3 passos', 'Protocolo completo'],
              ['Áudio guiado', '—', '6 áudios MP3'],
              ['Workbook PDF', '—', '1 por técnica'],
              ['Número de repetições', 'Limitado', 'Ilimitado'],
              ['Auto-save do progresso', '—', 'Incluído'],
              ['Suporte', '—', 'E-mail prioritário'],
              ['Acesso', 'Permanente (básico)', 'Vitalício (completo)'],
            ].map(([feature, free, paid], i) => (
              <div
                key={feature}
                className={`grid grid-cols-3 px-6 py-4 items-center ${
                  i % 2 === 0 ? 'bg-white' : 'bg-cream-50'
                }`}
              >
                <span className="text-sm text-charcoal-700">{feature}</span>
                <div className="text-center text-sm text-charcoal-400">
                  {free === '—' ? (
                    <i className="ri-close-line text-rose-400 text-base"></i>
                  ) : (
                    free
                  )}
                </div>
                <div className="text-center">
                  {paid.startsWith('✓') ? (
                    <i className="ri-check-line text-emerald-500 text-base"></i>
                  ) : (
                    <span className="text-sm font-semibold text-gold-700">{paid}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Depoimentos */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-display text-4xl font-bold text-charcoal-900 mb-3">
              Quem já fez, nunca mais foi o mesmo
            </h2>
            <div className="flex justify-center gap-1 mt-3">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-gold-500 text-lg"></i>
              ))}
              <span className="text-charcoal-500 text-sm ml-2 font-medium">4.9 de 5.0 · 847 avaliações</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-cream-50 border border-charcoal-100 rounded-2xl p-6 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-gold-200/50"
                  />
                  <div>
                    <div className="font-semibold text-charcoal-800 text-sm">{t.name}</div>
                    <div className="text-xs text-charcoal-400">{t.role}</div>
                  </div>
                </div>

                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.stars)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-gold-500 text-xs"></i>
                  ))}
                </div>

                <p className="text-sm text-charcoal-600 leading-relaxed flex-1 mb-4 italic">
                  &ldquo;{t.text}&rdquo;
                </p>

                <div className="inline-flex items-center gap-1.5 bg-gold-50 border border-gold-200 px-3 py-1.5 rounded-full">
                  <i className="ri-leaf-line text-gold-500 text-xs"></i>
                  <span className="text-xs font-semibold text-gold-700">{t.result}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Principal */}
      <section className="py-24 px-6 bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-gold-500/5 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <div className="w-10 h-px bg-gold-500/30 mx-auto mb-8"></div>
          <h2 className="font-display text-5xl font-bold text-white mb-5 leading-tight">
            Sua transformação<br />começa agora.
          </h2>
          <p className="text-white/55 text-lg font-light leading-relaxed mb-10 max-w-xl mx-auto">
            Por menos do que um jantar, você acessa os mesmos protocolos que coaches cobram R$500/hora para aplicar.
          </p>

          <div className="bg-white/5 border border-gold-500/15 rounded-2xl p-8 mb-8 inline-block w-full max-w-sm mx-auto">
            <div className="text-white/40 text-sm line-through mb-1">De R$ 108</div>
            <div className="font-display text-5xl font-bold text-gold-400 mb-1">R$ 47</div>
            <div className="text-white/40 text-xs mb-6">pagamento único · sem mensalidade</div>
            <Link
              to="/planos#exercicios-pnl"
              className="block w-full py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-xl font-bold text-base tracking-wide hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap"
            >
              Comprar Agora — R$ 47
            </Link>
            <p className="text-white/25 text-xs mt-4">
              Acesso imediato · Garantia 7 dias · Suporte incluso
            </p>
          </div>

          <div className="flex justify-center gap-6 text-xs text-white/30">
            <span className="flex items-center gap-1.5">
              <i className="ri-lock-line text-gold-500/50"></i>Pix / Cartão / Boleto
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-gold-500/50"></i>SSL Seguro
            </span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-white border-t border-charcoal-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-charcoal-900 text-center mb-10">
            Perguntas frequentes
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-charcoal-100 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-cream-50 transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-charcoal-800 text-sm pr-4">{faq.q}</span>
                  <i
                    className={`ri-arrow-down-s-line text-charcoal-400 flex-shrink-0 transition-transform duration-200 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-charcoal-500 font-light leading-relaxed border-t border-charcoal-50 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer mínimo */}
      <footer className="bg-charcoal-950 py-8 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-white/25 text-xs leading-relaxed">
            © 2026 Bia Holmes · Todos os direitos reservados ·{' '}
            <Link to="/privacidade" className="hover:text-white/50 transition-colors">Privacidade</Link>
            {' · '}
            <Link to="/termos" className="hover:text-white/50 transition-colors">Termos</Link>
          </p>
          <p className="text-white/15 text-xs mt-2">
            Os resultados podem variar. Os depoimentos refletem experiências individuais.
          </p>
        </div>
      </footer>
    </div>
  );
}
